'use client';

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import Link from 'next/link';

import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-wide transition duration-200 disabled:cursor-not-allowed disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-bright';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-r from-brand-deep via-brand to-brand-bright text-white shadow-[0_14px_40px_-16px_var(--color-brand)] hover:brightness-110 active:brightness-95',
  secondary: 'bg-surface-raised text-content border border-line-strong hover:border-brand hover:text-white',
  outline: 'border border-brand/60 text-brand-bright hover:bg-brand/10 hover:text-white',
  ghost: 'text-content-muted hover:text-white hover:bg-white/5',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-sm sm:text-base',
  lg: 'px-8 py-4 text-base',
};

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
  /** Stretch to the container width — useful for stacked mobile CTAs. */
  fullWidth?: boolean;
}

type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & { to?: never; href?: never };

type ButtonAsLink = CommonProps & { to: string; href?: never } & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    keyof CommonProps | 'href'
  >;

type ButtonAsAnchor = CommonProps & { href: string; to?: never } & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    keyof CommonProps
  >;

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

/**
 * One button, three renderings: a real `<button>`, an internal router `<Link>`
 * (`to`), or an external `<a>` (`href`). Keeps CTA styling identical wherever
 * it appears without duplicating class strings.
 */
export function Button({ variant = 'primary', size = 'md', className, fullWidth, children, ...props }: ButtonProps) {
  const classes = cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className);

  if ('to' in props && props.to !== undefined) {
    const { to, ...rest } = props;
    return (
      <Link href={to} className={classes} {...rest}>
        {children}
      </Link>
    );
  }

  if ('href' in props && props.href !== undefined) {
    const { href, target, rel, ...rest } = props;
    const isExternal = /^https?:/i.test(href);
    return (
      <a
        href={href}
        target={target ?? (isExternal ? '_blank' : undefined)}
        rel={rel ?? (isExternal ? 'noreferrer noopener' : undefined)}
        className={classes}
        {...rest}
      >
        {children}
      </a>
    );
  }

  const { type = 'button', ...rest } = props as ButtonAsButton;
  return (
    <button type={type} className={classes} {...rest}>
      {children}
    </button>
  );
}
